import * as React from "react";

export interface ReservedBounds {
    top?: number;
    right?: number;
    bottom?: number;
    left?: number;
}

export interface Identifiable<T> {
    id: number;
    value: T;
}

export interface ReservedBoundsContextModel {
    reserveBounds(bounds: ReservedBounds): number;
    releaseBounds(id: number): void;
}

const ReservedBoundsContext = React.createContext<ReservedBoundsContextModel>(undefined);

export function ReservedBoundsContextProvider({ children }) {
    const idRef = React.useRef(0);
    const newId = () => idRef.current++;

    const [reservedBounds, setReservedBounds] = React.useState<Identifiable<ReservedBounds>[]>([]);

    const maxReservedBounds = React.useMemo<ReservedBounds>(
        () =>
            reservedBounds
                .map(({ value }) => value)
                .reduce(
                    (a, b) => ({
                        top: Math.max(a.top || 0, b.top || 0),
                        right: Math.max(a.right || 0, b.right || 0),
                        bottom: Math.max(a.bottom || 0, b.bottom || 0),
                        left: Math.max(a.left || 0, b.left || 0),
                    }),
                    { top: 0, right: 0, bottom: 0, left: 0 },
                ),
        [reservedBounds],
    );

    const contextValue = React.useRef({
        reserveBounds: (bounds: ReservedBounds) => {
            const id = newId();
            setReservedBounds((state) => [...state, { id, value: bounds }]);
            return id;
        },
        releaseBounds: (id: number) => {
            setReservedBounds((state) => [...state.filter((b) => b.id !== id)]);
        },
    });

    return (
        <ReservedBoundsContext.Provider value={contextValue.current}>
            {children(maxReservedBounds)}
        </ReservedBoundsContext.Provider>
    );
}

export default function useReservedBounds(bounds: ReservedBounds, deps: any[]) {
    const { reserveBounds, releaseBounds } = React.useContext(ReservedBoundsContext);
    React.useEffect(() => {
        const id = reserveBounds(bounds);
        return () => {
            releaseBounds(id);
        };
    }, deps);
}
